import { ActionTypes } from "../../../OrderFlowProvider/OrderFlowActionTypes";
import { getTemplateDays } from "../../../utils/orderFlowUtils";
/**
 * Handles mutually exclusive selection of meal bundles
 *
 * @param dispatch
 * @param storeCartBundles
 * @returns
 */
export const handleDispatchToggleMealBundle = (dispatch, programMeals, storeCartTemplate, storeCartBundles) => (selectedBundle) => {
    //If the same bundle is selected, dont do anything
    if (storeCartBundles === null || storeCartBundles === void 0 ? void 0 : storeCartBundles.find((bundle) => bundle.BundleName === selectedBundle.BundleName))
        return;
    selectedBundle.Quantity = 1;
    const selectedBundleWithUpdatedMealQuantity = updateBundleMealQty([selectedBundle]);
    const templateFromBundle = createTemplateFromBundle([selectedBundle]);
    let existingTemplate = storeCartTemplate;
    //Get the previously selected bundle
    const bundleToRemove = storeCartBundles === null || storeCartBundles === void 0 ? void 0 : storeCartBundles.find((bundle) => bundle.BundleName !== selectedBundle.BundleName);
    if (bundleToRemove) {
        existingTemplate = removeBundleMealsFromTemplate(bundleToRemove, programMeals, existingTemplate);
    }
    const sortedBundleTemplate = [...existingTemplate, ...templateFromBundle].sort((a, b) => a.DayOfWeek - b.DayOfWeek);
    selectedBundleWithUpdatedMealQuantity[0].BundleTemplate = sortedBundleTemplate;
    // dayQuantity is set from the bundle only in single-select mode, 
    // and only if the bundle has a defined Day Quantity.
    if (selectedBundle.EnableDayQuantity && selectedBundleWithUpdatedMealQuantity[0].BundleDayQuantity) {
        dispatch({
            type: ActionTypes.SET_DAY_QUANTITY,
            payload: selectedBundleWithUpdatedMealQuantity[0].BundleDayQuantity,
        });
    }
    dispatch({
        type: ActionTypes.SET_MEAL_BUNDLE,
        payload: selectedBundleWithUpdatedMealQuantity,
    });
    dispatch({
        type: ActionTypes.SET_RECIPE_FETCH_STATUS,
        payload: { error: false, completed: false },
    });
};
/**
 * Handles adding a new meal bundle or incrementing an exisitng bundle.
 *
 * @param dispatch
 * @param storeCartBundles
 * @returns
 */
export const handleDispatchIncrementMealBundleQuantity = (dispatch, getMealDatesForBundle, programMeals, storeCartTemplate, defaultDayQuantity, storeCartBundles) => (selectedBundle) => {
    var _a;
    const dayQuantity = (_a = Number(selectedBundle.BundleDayQuantity)) !== null && _a !== void 0 ? _a : defaultDayQuantity;
    const mealDatesForBundle = getMealDatesForBundle(dayQuantity);
    const mealDaysForBundle = [...new Set(mealDatesForBundle.map((mealDate) => mealDate.day()))];
    const existingBundle = storeCartBundles === null || storeCartBundles === void 0 ? void 0 : storeCartBundles.find((bundle) => bundle.BundleName === selectedBundle.BundleName);
    selectedBundle.Quantity = existingBundle ? existingBundle.Quantity + 1 : 1;
    let newBundles = [selectedBundle];
    let existingCartTemplate = storeCartTemplate;
    let updatedTemplate = [];
    if (storeCartBundles && storeCartBundles.length > 0) {
        //If the bundle exists update it
        //If it doesnt exist, append the new bundle
        newBundles = existingBundle
            ? storeCartBundles.map((bundle) => (Object.assign(Object.assign({}, bundle), { Quantity: bundle.BundleName === selectedBundle.BundleName ? selectedBundle.Quantity : bundle.Quantity })))
            : [...storeCartBundles, selectedBundle];
    }
    const bundlesWithNewQuantity = updateBundleMealQty(newBundles);
    const templateFromNewBundles = createTemplateFromBundle(bundlesWithNewQuantity);
    // Set the template for the selected bundle
    const selectedBundleWithQuantity = updateBundleMealQty([selectedBundle]);
    selectedBundle.BundleTemplate = createTemplateFromBundle(selectedBundleWithQuantity);
    //If a bundle meal exists in the template, update its quantity to the new bundle qty
    existingCartTemplate.forEach((template) => {
        let quantity = template.Quantity;
        const bundleMealExists = templateFromNewBundles.find((bt) => bt.ProgramMealId === template.ProgramMealId && bt.DayOfWeek === template.DayOfWeek);
        if (bundleMealExists) {
            quantity = bundleMealExists.Quantity;
        }
        updatedTemplate.push(Object.assign(Object.assign({}, template), { Quantity: quantity }));
    });
    //Go through bundle template and add meals that are not already in the template
    templateFromNewBundles.forEach((bt) => {
        const mealExists = Boolean(updatedTemplate.find((t) => t.ProgramMealId === bt.ProgramMealId &&
            t.DayOfWeek === bt.DayOfWeek &&
            mealDaysForBundle.includes(t.DayOfWeek)));
        if (!mealExists) {
            updatedTemplate.push(Object.assign(Object.assign({}, bt), { Quantity: bt.Quantity }));
        }
    });
    dispatch({
        type: ActionTypes.SET_MEAL_BUNDLE,
        payload: newBundles,
    });
    // if bundle didnt exist before, fetch meals
    if (!existingBundle) {
        dispatch({
            type: ActionTypes.SET_RECIPE_FETCH_STATUS,
            payload: { error: false, completed: false },
        });
    }
};
/**
 * Handles decrementing the quantity of an existing bundle.
 *
 * @param dispatch
 * @param storeCartBundles
 * @returns
 */
export const handleDispatchDecrementMealBundleQuantity = (dispatch, programMeals, storeCartTemplate, storeCartBundles) => (selectedBundle) => {
    let existingTemplate = storeCartTemplate;
    let updatedTemplate = [];
    let shouldRefetch = false;
    const updatedBundles = storeCartBundles.map((bundle) => {
        if (bundle.BundleName === selectedBundle.BundleName) {
            bundle.Quantity -= 1;
        }
        // if bundle being removed, fetch meals
        // to handle case where no bundles are selected & user can now choose any meal
        if (bundle.Quantity < 1) {
            shouldRefetch = true;
        }
        return bundle;
    });
    const updatedBundlesWithQty = updateBundleMealQty(updatedBundles);
    const templateFromUpdatedBundles = createTemplateFromBundle(updatedBundlesWithQty);
    existingTemplate.forEach((template) => {
        let quantity = template.Quantity;
        const bundleMealExists = templateFromUpdatedBundles.find((bt) => bt.ProgramMealId === template.ProgramMealId && bt.DayOfWeek === template.DayOfWeek);
        if (bundleMealExists) {
            quantity = bundleMealExists.Quantity;
        }
        updatedTemplate.push(Object.assign(Object.assign({}, template), { Quantity: quantity }));
    });
    dispatch({
        type: ActionTypes.SET_MEAL_BUNDLE,
        payload: updatedBundles,
    });
    if (shouldRefetch) {
        dispatch({
            type: ActionTypes.SET_RECIPE_FETCH_STATUS,
            payload: { error: false, completed: false },
        });
    }
};
/**
 * Multiplies the bundle quantity and the bundle meal quantity to get the correct quantity for meals
 *
 * @param bundles
 * @returns
 */
export const updateBundleMealQty = (bundles) => {
    return bundles.map((bundle) => {
        return Object.assign(Object.assign({}, bundle), { BundleMeals: bundle.BundleMeals.map((bm) => {
                return Object.assign(Object.assign({}, bm), { Quantity: bm.Quantity * bundle.Quantity });
            }) });
    });
};
/**
 * Transforms bundle meals into a template object
 *
 * @param bundles
 * @param programMeals
 * @returns
 */
export const createTemplateFromBundle = (bundles) => {
    const template = bundles.flatMap((bundle) => {
        return bundle.BundleMeals.map((bm) => ({
            ProgramMealId: bm.ProgramMealId,
            ProgramMealName: bm.ProgramMealName,
            Price: bm.Price,
            DayOfWeek: bm.DayOfWeek,
            Quantity: bm.Quantity * bundle.Quantity,
            MealSize: bm === null || bm === void 0 ? void 0 : bm.MealSize,
            MealTypeId: bm === null || bm === void 0 ? void 0 : bm.MealTypeId
        }));
    });
    return resolveDuplicates(template);
};
/**
 * Reduce the quantity of duplicate day/meal combos in the template
 *
 * @param existingTemplate
 * @returns
 */
export const resolveDuplicates = (existingTemplate) => {
    let newTemplate = existingTemplate;
    const programMealIds = [...new Set(existingTemplate.map((sm) => sm.ProgramMealId))];
    const daysOfWeek = [...new Set(existingTemplate.map((sm) => sm.DayOfWeek))];
    programMealIds.forEach((programMealId) => {
        daysOfWeek.forEach((dayOfWeek) => {
            let duplicates = newTemplate.filter((sm) => sm.ProgramMealId === programMealId && sm.DayOfWeek === dayOfWeek);
            if (duplicates.length > 1) {
                let newQuantity = duplicates.reduce((acc, sm) => acc + sm.Quantity, 0);
                newTemplate = newTemplate.filter((sm) => sm.ProgramMealId !== programMealId || sm.DayOfWeek !== dayOfWeek);
                newTemplate.push({
                    ProgramMealId: programMealId,
                    Quantity: newQuantity,
                    DayOfWeek: dayOfWeek,
                    ProgramMealName: duplicates[0].ProgramMealName,
                    Price: duplicates[0].Price,
                    MealSize: duplicates[0].MealSize,
                    MealTypeId: duplicates[0].MealTypeId
                });
            }
        });
    });
    return newTemplate;
};
/**
 * Removes specific meals from the template based on the specified bundle.
 * This function identifies which meals need to be removed by comparing the `ProgramMealId` and `DayOfWeek`
 * from the `bundle.BundleMeals` against those in the `existingTemplate`. If a match is found, those meals
 * are excluded from the returned array.
 *
 * @param {Bundle} bundle
 * @param {ProgramMeal[]} programMeals
 * @param {SubscriptionMeal[]}existingTemplate
 * @returns {SubscriptionMeal[]}
 */
const removeBundleMealsFromTemplate = (bundle, programMeals, existingTemplate) => {
    const mealsToRemove = bundle.BundleMeals.map((meal) => {
        var _a;
        return (Object.assign(Object.assign({}, meal), { MealSize: (_a = programMeals.find((pm) => pm.ProgramMealId === meal.ProgramMealId)) === null || _a === void 0 ? void 0 : _a.MealSize }));
    });
    return existingTemplate.filter((template) => !mealsToRemove.some((meal) => meal.ProgramMealId === template.ProgramMealId && template.DayOfWeek === meal.DayOfWeek));
};
/**
 * Processes each bundle separately to generate meals and a template based on the bundle day quantity. It then combines all bundles into a single template.
 * This calls 'getMealsFromTemplate' so we need to pass in the appropriate parameters to update the store as expected for each reducer case this is called in.
 *
 * @param {Store} store
 * @param {Bundle[]} bundles
 * @param {GetMealsFromTemplate}
 * @param {Date} startDate
 * @param {boolean} isSubscription
 * @param {number[]} daysOfWeek
 * @returns {{meals: Meal[], template: SubscriptionMeal[]}}
 */
export const processBundles = (cartDayQuantity, deliveryDays, startSunday, subscriptionPeriod, oneTimeOrderPeriod, onlyShowDeliveryDays, cartMeals, allBundleMeals, selectedBundles, getMealsFromTemplate, startDate, isSubscription, daysOfWeek) => {
    let template = [];
    const mealsMap = new Map();
    selectedBundles.forEach((bundle) => {
        const dayQuantity = bundle.EnableDayQuantity ? Number(bundle.BundleDayQuantity) : cartDayQuantity;
        const templateDays = getTemplateDays(daysOfWeek, startDate, dayQuantity);
        const filteredBundleTemplate = bundle.BundleTemplate.filter((bt) => templateDays.includes(bt.DayOfWeek));
        // Each bundle can have a different day quantity so this needs to be called for each one
        const mealsFromTemplate = getMealsFromTemplate(filteredBundleTemplate, daysOfWeek, dayQuantity, deliveryDays, startDate, startSunday, subscriptionPeriod, oneTimeOrderPeriod, isSubscription, onlyShowDeliveryDays, allBundleMeals, cartMeals);
        const mealWithUpdatedQuantity = mealsFromTemplate.map((meal) => (Object.assign(Object.assign({}, meal), { Quantity: meal.Quantity * bundle.Quantity })));
        mealWithUpdatedQuantity.forEach((m) => {
            const key = `${m.ProgramMealId}-${m.MealDate}`;
            const existing = mealsMap.get(key);
            if (existing) {
                existing.Quantity += m.Quantity; // add to quantity, don’t duplicate
            }
            else {
                mealsMap.set(key, Object.assign({}, m));
            }
        });
        template = [
            ...template,
            ...filteredBundleTemplate.map((bt) => (Object.assign(Object.assign({}, bt), { Quantity: bundle.Quantity * bt.Quantity }))),
        ];
    });
    const meals = Array.from(mealsMap.values()).filter(m => m.Quantity > 0);
    return { meals: meals, template: template };
};
